/**
 * XLSX / CSV export utilities
 *
 * Uses the SheetJS (xlsx) library that is already installed for import parsing.
 * All values are written as text so that phone numbers, zip codes, and
 * leading-zero strings are never silently converted to numbers by Excel.
 */

import * as XLSX from 'xlsx'

/** Download helper — creates an ephemeral <a> and clicks it */
function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Export an array of row objects as an .xlsx file.
 *
 * - Every cell is forced to text (`t: 's'`) so phone numbers like
 *   +91-22-4001-0001 are never converted to scientific notation.
 * - Column widths are auto-sized based on header + longest value (capped).
 */
export function exportAsXLSX(
  data: Record<string, string>[],
  fileName: string,
  sheetName = 'Sheet1',
) {
  if (data.length === 0) return

  const headers = Object.keys(data[0])

  // Build worksheet from JSON — then force every cell to string type
  const ws = XLSX.utils.json_to_sheet(data)

  // Force all data cells to text so Excel doesn't reinterpret them
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1')
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r, c })
      const cell = ws[addr]
      if (cell && cell.t !== 's') {
        cell.t = 's'
        cell.v = String(cell.v ?? '')
        delete cell.w // remove cached formatted text so Excel re-renders
      }
    }
  }

  // Auto-size column widths (capped at 40 chars)
  ws['!cols'] = headers.map((h, colIdx) => {
    let maxLen = h.length
    for (let r = 0; r < Math.min(data.length, 200); r++) {
      const val = String(data[r][h] ?? '')
      if (val.length > maxLen) maxLen = val.length
    }
    return { wch: Math.min(maxLen + 2, 40) }
  })

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheetName)

  const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([wbOut], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  const safeName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`
  downloadBlob(blob, safeName)
}

/**
 * Export an array of row objects as a .csv file.
 */
// #186 SECURITY (CSV/Excel formula injection): a cell whose first character is
// `=` `+` `-` `@`, tab or CR is executed as a formula by Excel / Google Sheets /
// LibreOffice (e.g. `=cmd|'/C calc'!A0`, DDE can launch a process). Import files
// arrive from outside the security team; a hostile row survives the review,
// lands in an export, and runs when the analyst opens it. Prefix with an
// apostrophe so the cell is literal text (OWASP), then apply standard CSV
// quote-escaping. Single source of truth — reused by the skipped-rows export
// and the import-history errors export. Matches csv-generator.ts::escapeCSVValue.
export function escapeCsvCell(raw: string | number): string {
  let value = String(raw ?? '')
  if (/^[=+\-@\t\r]/.test(value)) {
    value = `'${value}`
  }
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

export function exportAsCSV(
  data: Record<string, string>[],
  fileName: string,
) {
  if (data.length === 0) return

  const headers = Object.keys(data[0])

  const csvContent = [
    headers.join(','),
    ...data.map((row) =>
      headers.map((h) => escapeCsvCell(row[h] ?? '')).join(','),
    ),
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const safeName = fileName.endsWith('.csv') ? fileName : `${fileName}.csv`
  downloadBlob(blob, safeName)
}
